import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, apiClient } from "@/lib/api";
import { Send, X, Mic, Volume2, VolumeX, Sparkles, MessageCircle, Minimize2, Settings, Globe } from "lucide-react";
import ClaretOnboardingModal from "./ClaretOnboardingModal";

const MOOD_EMOJIS = {
  Excited: "🤩", Happy: "😊", Motivated: "💪", Calm: "😌", Neutral: "😐",
  Tired: "😴", Anxious: "😰", Stressed: "😓", Sad: "😢", Frustrated: "😤", Overwhelmed: "🤯",
};

const ClaretChatWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [currentMood, setCurrentMood] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileChecked, setProfileChecked] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-open Claret when a reminder/notification fires
  useEffect(() => {
    if (!user) return;
    const checkNotifications = async () => {
      try {
        const res = await apiClient.get('/notifications?unread=true&limit=5');
        const notifs = res.data?.notifications || res.data || [];
        const claretNotif = notifs.find(n => 
          (n.type === 'reminder' || n.link === '/claret') && !n.read
        );
        if (claretNotif && !isOpen) {
          setIsOpen(true);
          // Auto-add the notification as a Claret message
          setMessages(prev => {
            const alreadyShown = prev.some(m => m.notifId === claretNotif.id);
            if (alreadyShown) return prev;
            return [...prev, {
              role: "assistant",
              message: `**Reminder:** ${claretNotif.message || claretNotif.title}`,
              time: new Date().toISOString(),
              notifId: claretNotif.id,
              isReminder: true,
            }];
          });
          // Mark as read
          try { await apiClient.put(`/notifications/${claretNotif.id}/read`); } catch {}
        }
      } catch {}
    };
    const interval = setInterval(checkNotifications, 60000);
    return () => clearInterval(interval);
  }, [user, isOpen]);

  useEffect(() => {
    if (user) {
      const sid = `claret-${user.id}-${new Date().toISOString().slice(0, 10)}`;
      setSessionId(sid);
      // Check if user has a Claret profile
      apiClient.get(`/claret/profile?user_id=${user.id}`)
        .then(res => {
          setProfile(res.data.profile);
          setProfileChecked(true);
        })
        .catch(() => setProfileChecked(true));
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && profileChecked && !profile) {
      setShowOnboarding(true);
    }
  }, [isOpen, profileChecked, profile]);

  useEffect(() => {
    if (isOpen && sessionId && messages.length === 0 && profile) {
      showWelcomeAndHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId, profile]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const showWelcomeAndHistory = async () => {
    const nick = profile?.nickname || user?.full_name?.split(" ")[0] || "there";
    const lang = profile?.language || "english";

    const welcomeMessages = {
      english: `Hey ${nick}! How are you today?\n\nWhat would you like to ask?`,
      hinglish: `Hey ${nick}! Kaise ho aaj?\n\nKya poochna hai?`,
      manglish: `Hey ${nick}! Innu engane und?\n\nEnthanu chodikkanam?`,
    };

    const welcomeMsg = {
      role: "assistant",
      message: welcomeMessages[lang] || welcomeMessages.english,
      time: new Date().toISOString(),
      isWelcome: true,
      welcomeActions: [
        { label: lang === "hinglish" ? "ERP Related (Leads, Students, Commission...)" : lang === "manglish" ? "ERP Related (Leads, Students, Commission...)" : "ERP Related (Leads, Students, Commissions...)", type: "erp" },
        { label: lang === "hinglish" ? "Non-ERP (Sales Tips, Market, Kuch bhi!)" : lang === "manglish" ? "Non-ERP (Sales Tips, Market, Enthum!)" : "Non-ERP (Sales Tips, Market, Anything!)", type: "general" },
      ],
    };

    // Always show welcome first, then load any recent history below
    try {
      const res = await apiClient.get(`/claret/chat/history?session_id=${sessionId}&limit=30`);
      if (res.data.chats?.length > 0) {
        const history = res.data.chats.map(c => ({
          role: c.role,
          message: c.message,
          mood_scores: c.mood_scores,
          time: c.created_at,
        }));
        const lastMood = res.data.chats.filter(c => c.mood_scores?.mood_label).pop();
        if (lastMood) setCurrentMood(lastMood.mood_scores);
        setMessages([welcomeMsg, ...history]);
      } else {
        setMessages([welcomeMsg]);
      }
    } catch {
      setMessages([welcomeMsg]);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", message: userMsg, time: new Date().toISOString() }]);
    setLoading(true);

    try {
      // Use streaming endpoint — backend handles web search internally
      const token = localStorage.getItem("access_token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const response = await fetch(`${backendUrl}/api/claret/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: userMsg,
          session_id: sessionId,
          user_role: user?.role || "",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Add an empty assistant message that we'll stream into
      const streamMsgId = Date.now();
      setMessages(prev => [...prev, {
        role: "assistant",
        message: "",
        _streamId: streamMsgId,
        _streaming: true,
        time: new Date().toISOString(),
      }]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let rawContent = "";
      let finalMood = null;
      let finalActions = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.type === "status") {
              setStatusText(payload.text || "");
            } else if (payload.type === "token") {
              setStatusText(""); // Clear status once tokens start
              rawContent += payload.content;
              // Extract just the message text from potential JSON
              let displayText = rawContent;
              // Try to parse as JSON to extract "message" field in real-time
              try {
                const jsonMatch = rawContent.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                  const parsed = JSON.parse(jsonMatch[0]);
                  if (parsed.message) displayText = parsed.message;
                }
              } catch {
                // Not valid JSON yet — show raw text but strip JSON artifacts
                displayText = rawContent.replace(/^\s*\{?\s*"message"\s*:\s*"?/, "").replace(/"\s*,?\s*"mood_scores[\s\S]*$/, "");
              }
              setMessages(prev => prev.map(m =>
                m._streamId === streamMsgId ? { ...m, message: displayText } : m
              ));
            } else if (payload.type === "done") {
              setStatusText("");
              finalMood = payload.mood_scores || null;
              finalActions = payload.suggested_actions || [];
            }
          } catch { /* skip malformed lines */ }
        }
      }

      // Finalize the streamed message
      setMessages(prev => prev.map(m =>
        m._streamId === streamMsgId ? {
          ...m,
          _streaming: false,
          mood_scores: finalMood,
          suggested_actions: finalActions,
        } : m
      ));
      if (finalMood?.mood_label) setCurrentMood(finalMood);

    } catch (err) {
      setStatusText("");
      // Fallback to non-streaming endpoint on any error
      try {
        const res = await apiClient.post("/claret/chat", {
          message: userMsg,
          session_id: sessionId,
          user_role: user?.role || "",
        }, { timeout: 45000 });
        setMessages(prev => {
          const filtered = prev.filter(m => !m._streaming);
          return [...filtered, {
            role: "assistant",
            message: res.data.message,
            mood_scores: res.data.mood_scores,
            suggested_actions: res.data.suggested_actions,
            time: new Date().toISOString(),
          }];
        });
        if (res.data.mood_scores?.mood_label) setCurrentMood(res.data.mood_scores);
      } catch {
        setMessages(prev => {
          const filtered = prev.filter(m => !m._streaming);
          return [...filtered, {
            role: "assistant",
            message: "I hit a small bump — but I'm still here! Try sending your message again.",
            time: new Date().toISOString(),
            isError: true,
          }];
        });
      }
    }
    setLoading(false);
  };

  const speakText = (text) => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.pitch = 1.05;
    utterance.onend = () => setIsSpeaking(false);
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  const translateToMalayalam = async (text) => {
    setLoading(true);
    try {
      const res = await apiClient.post("/claret/chat", {
        message: `Translate this to Malayalam: "${text}"`,
        session_id: sessionId,
      });
      setMessages(prev => [...prev, {
        role: "assistant",
        message: res.data.message,
        time: new Date().toISOString(),
      }]);
    } catch {}
    setLoading(false);
  };

  if (!user) return null;

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Refresh profile
    apiClient.get(`/claret/profile?user_id=${user.id}`)
      .then(res => setProfile(res.data.profile))
      .catch(() => {});
  };

  return (
    <>
      {/* Onboarding Modal */}
      {showOnboarding && (
        <ClaretOnboardingModal
          userId={user.id}
          userName={user.full_name || ""}
          onComplete={handleOnboardingComplete}
        />
      )}

      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group"
          data-testid="claret-toggle-btn"
        >
          <Sparkles className="w-6 h-6 group-hover:animate-pulse" />
          {currentMood && (
            <span className="absolute -top-1 -right-1 text-sm">{MOOD_EMOJIS[currentMood.mood_label] || "😊"}</span>
          )}
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-6 left-6 z-50 w-[380px] h-[560px] rounded-2xl shadow-2xl border border-border/50 flex flex-col overflow-hidden bg-background" data-testid="claret-chat-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold text-sm leading-tight">Claret</p>
                <p className="text-[10px] text-white/70">Your AI buddy at CLT</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {currentMood && (
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full mr-1">
                  {MOOD_EMOJIS[currentMood.mood_label] || "😊"} {currentMood.mood_label}
                </span>
              )}
              <button onClick={() => setShowOnboarding(true)} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" title="Settings">
                <Settings className="w-4 h-4" />
              </button>
              <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors">
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="claret-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : msg.role === "system" ? "justify-center" : "justify-start"}`}>
                {msg.role === "system" && msg.isSearching ? (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full animate-pulse">
                    <Globe className="w-3 h-3 animate-spin" /> Searching the web...
                  </div>
                ) : (
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                  msg.role === "user"
                    ? "bg-indigo-600 text-white rounded-br-sm"
                    : "bg-muted rounded-bl-sm"
                }`}>
                  {msg.hasWebData && (
                    <div className="flex items-center gap-1 text-[10px] text-indigo-500 mb-1">
                      <Globe className="w-3 h-3" /> Powered by live web data
                    </div>
                  )}
                  {msg.isReminder && (
                    <div className="flex items-center gap-1 text-[10px] text-amber-500 mb-1">
                      <Sparkles className="w-3 h-3" /> Reminder
                    </div>
                  )}
                  {msg.reminderSet && (
                    <div className="mt-1.5 p-1.5 bg-amber-500/10 rounded text-[10px] text-amber-600 border border-amber-500/20">
                      Reminder set for {new Date(msg.reminderSet.reminder_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </div>
                  )}
                  <p className="whitespace-pre-wrap">{msg.message}{msg._streaming && <span className="inline-block w-1.5 h-4 bg-indigo-500 ml-0.5 animate-pulse rounded-sm align-middle" />}</p>
                  {msg.isWelcome && msg.welcomeActions && (
                    <div className="flex flex-col gap-2 mt-3">
                      {msg.welcomeActions.map((action, j) => (
                        <button
                          key={j}
                          onClick={() => {
                            // Remove the welcome buttons after selection
                            setMessages(prev => prev.map(m =>
                              m.isWelcome ? { ...m, welcomeActions: null, selectedType: action.type } : m
                            ));
                            if (action.type === "erp") {
                              setInput("");
                              inputRef.current?.focus();
                              inputRef.current?.setAttribute("placeholder", "Ask about leads, students, commissions, attendance...");
                            } else {
                              setInput("");
                              inputRef.current?.focus();
                              inputRef.current?.setAttribute("placeholder", "Ask about sales tips, market trends, anything...");
                            }
                          }}
                          className={`w-full text-left text-xs px-3 py-2.5 rounded-xl border transition-all hover:scale-[1.02] ${
                            action.type === "erp"
                              ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-600 hover:bg-indigo-500/20"
                              : "bg-emerald-500/10 border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/20"
                          }`}
                          data-testid={`claret-welcome-${action.type}`}
                        >
                          <span className="font-medium">{action.type === "erp" ? "📊" : "💬"} {action.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {msg.isWelcome && msg.selectedType && (
                    <div className={`mt-2 text-[10px] px-2 py-1 rounded-full inline-block ${
                      msg.selectedType === "erp" ? "bg-indigo-500/10 text-indigo-500" : "bg-emerald-500/10 text-emerald-500"
                    }`}>
                      {msg.selectedType === "erp" ? "📊 ERP Mode" : "💬 General Mode"} — Go ahead, type your question!
                    </div>
                  )}
                  {msg.role === "assistant" && !msg.isWelcome && (
                    <div className="flex gap-1 mt-1.5 pt-1 border-t border-border/20">
                      <button
                        onClick={() => speakText(msg.message)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-background/50 hover:bg-background/80 transition-colors flex items-center gap-0.5"
                        title="Read aloud"
                      >
                        {isSpeaking ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                        {isSpeaking ? "Stop" : "Listen"}
                      </button>
                      <button
                        onClick={() => translateToMalayalam(msg.message)}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-background/50 hover:bg-background/80 transition-colors"
                        title="Translate to Malayalam"
                      >
                        ML
                      </button>
                    </div>
                  )}
                  {msg.suggested_actions?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {msg.suggested_actions.map((a, j) => (
                        <button
                          key={j}
                          onClick={() => { setInput(a); inputRef.current?.focus(); }}
                          className="text-[10px] px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20 transition-colors"
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                )}
              </div>
            ))}
            {loading && !messages.some(m => m._streaming) && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center gap-2">
                    {statusText ? (
                      <>
                        <div className="relative w-5 h-5 shrink-0">
                          <span className="absolute inset-0 rounded-full bg-indigo-500/30 animate-ping" />
                          <span className="absolute inset-0.5 rounded-full bg-indigo-500/60 animate-pulse" />
                          <span className="absolute inset-1 rounded-full bg-indigo-500" />
                        </div>
                        <span className="text-xs text-muted-foreground font-medium animate-pulse">{statusText}</span>
                      </>
                    ) : (
                      <>
                        <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-3 pb-3 pt-1 shrink-0">
            <div className="flex items-center gap-2 bg-muted/50 rounded-xl border px-3 py-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && sendMessage()}
                placeholder="Chat with Claret..."
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
                data-testid="claret-input"
                disabled={loading}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="p-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 transition-all"
                data-testid="claret-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClaretChatWidget;
